import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isExternalUrl, resolveLoSiteLink } from '@/lib/lyrraone-site-links';
import { cn } from '@/lib/utils';

export function SiteThemeLink({
  href,
  className,
  style,
  onClick,
  onDoubleClick,
  children,
  editMode,
}: {
  href: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  editMode?: boolean;
}) {
  const handleClick = (e: React.MouseEvent) => {
    if (editMode) {
      e.preventDefault();
      onClick?.(e);
    }
  };

  if (isExternalUrl(href)) {
    return (
      <a
        href={href}
        className={className}
        style={style}
        target={editMode ? undefined : '_blank'}
        rel={editMode ? undefined : 'noopener noreferrer'}
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} style={style} onClick={handleClick} onDoubleClick={onDoubleClick}>
      {children}
    </Link>
  );
}

interface ThemeEditableProps {
  elementKey: string;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  as?: keyof JSX.IntrinsicElements;
  children: React.ReactNode;
  editableText?: boolean;
}

export function ThemeEditable({
  elementKey,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  className,
  style,
  as: Tag = 'div',
  children,
  editableText,
}: ThemeEditableProps) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const selected = editMode && selectedElementKey === elementKey;

  useEffect(() => {
    if (!editing) return;
    ref.current?.focus();
  }, [editing]);

  const handleBlur = useCallback(() => {
    if (!editing || !ref.current) return;
    setEditing(false);
    onInlineTextChange?.(elementKey, ref.current.innerText.trim());
  }, [editing, elementKey, onInlineTextChange]);

  const props: Record<string, unknown> = {
    'data-lo-element': elementKey,
    className: cn(className, editMode && 'lo-site-editable', selected && 'lo-site-element-selected'),
    style,
    onClick: (e: React.MouseEvent) => {
      if (!editMode) return;
      e.stopPropagation();
      onSelectElement?.(elementKey);
    },
    onDoubleClick: (e: React.MouseEvent) => {
      if (!editMode || !editableText) return;
      e.stopPropagation();
      onSelectElement?.(elementKey);
      setEditing(true);
    },
  };

  if (editMode && editableText && editing) {
    props.contentEditable = true;
    props.suppressContentEditableWarning = true;
    props.onBlur = handleBlur;
    props.onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        ref.current?.blur();
      }
      if (e.key === 'Escape') setEditing(false);
    };
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <Tag ref={ref as never} {...props}>
      {children}
    </Tag>
  );
}

export function ThemeButtonText({
  elementKey,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  children,
}: {
  elementKey: string;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <span
      onDoubleClick={(e) => {
        if (!editMode) return;
        e.stopPropagation();
        onSelectElement?.(elementKey);
        const span = e.currentTarget;
        span.contentEditable = 'true';
        span.focus();
        const onBlur = () => {
          span.contentEditable = 'false';
          onInlineTextChange?.(elementKey, span.innerText.trim());
          span.removeEventListener('blur', onBlur);
        };
        span.addEventListener('blur', onBlur);
      }}
    >
      {children}
    </span>
  );
}
